import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@stores/authStore';
import { useUnreadStore, isTaskUnread, taskUnreadKey } from '@stores/unreadStore';
import {
  getAgentStatusRoster,
  listAgentTasks,
  markAgentTaskRead,
  type AgentTask,
} from '@lib/services/storeManager/agentTaskApi';
import { listApprovals } from '@lib/services/storeManager/approvalApi';
import {
  CHAT_READ_EVENT,
  getChatUnreadSummary,
  type ChatReadEventDetail,
  type ChatUnreadSummary,
} from '@lib/services/agents/wsChatApi';
import { ATTENTION_ACKED_EVENT, ackedTaskIds, consumeAck } from '@lib/services/storeManager/attentionAck';

/**
 * How long a just-read chat stays locally discounted before the server's unread summary must carry
 * it on its own. Long enough to cover the read round-trip (POST + the repull it triggers), short
 * enough that a genuinely new agent message is never masked for more than a moment. Below the 30s
 * poll on purpose. See `chatReadLocally` in unreadStore.
 */
const CHAT_READ_TTL_MS = 12_000;

/** Where a red dot should take the merchant, and which failure it is about. */
export interface AgentAttentionTarget {
  /** The task the dot is about — what gets acknowledged once they have seen it. */
  taskId?: string | null;
  /** The conversation to open. Absent for a scheduled deliverable, which ran in no conversation. */
  conversationId?: string | null;
  /** True when the dot is a FAILURE (dismissible by reading) rather than a pending approval. */
  failed: boolean;
}

export interface AgentAttention {
  /** agentType → the agent has something the merchant has not seen: an unread task or an unread chat. */
  attentionByAgent: Record<string, boolean>;
  /**
   * agentType → what the agent's most pressing task is, so clicking the row can open the
   * conversation the failure happened in instead of a blank new chat (#6).
   */
  targetByAgent: Record<string, AgentAttentionTarget>;
  /** Any task at all is unread — the Task Center row's red dot. */
  taskCenterAttention: boolean;
  /** Any approval is still pending — kept for callers that still ask the old question. */
  approvalsAttention: boolean;
  /**
   * Infra 1.5 #19 —— Task Center 行的待处理数角标。
   *
   * <p>审批中心废除后，「待处理」是任务的一个状态，暴露方式改为顶部筛选 ＋ 这个角标（PRD #19 明说
   * 不做默认视图置顶，因为会和 #18 的 agent 分组冲突）。
   *
   * <p>数字来自 roster 的 `AgentStatusResponse.pendingCount` 之和 —— `agent_tasks` 里
   * `NEEDS_ACTION` ＋ 遗留 `AWAITING_APPROVAL` 的条数，也正是任务中心「待处理」页签筛的那个集合，
   * 所以角标与它点开的那一屏是同一份答案。旧的口径是审批队列（`content_drafts` 的投影）＋
   * 一份 dev-only 的 mock；那个 mock 文件已删。
   *
   * <p>⚠️ 与旧口径不是同一个集合，是它的**超集**：批准但还没发布的草稿仍然留在
   * `NEEDS_ACTION`（`agent_tasks_release_decided_approvals.yaml` 明写「APPROVED → 不动，
   * 『待发布』对它仍然成立」），而它一批准就离开审批队列；父批次行也会滚算到这个状态。
   */
  pendingCount: number;
}

interface Snapshot {
  /** Whose answer this is. A fetch that fails must fall back to the LAST answer, never another
   *  merchant's — accounts switch inside one tab and this hook does not unmount when they do. */
  tenantId: string | null;
  tasks: AgentTask[];
  unreadChats: ChatUnreadSummary;
  targetByAgent: Record<string, AgentAttentionTarget>;
  approvalsAttention: boolean;
  /** [Infra 1.5 #19] 待处理条数。见 {@link AgentAttention.pendingCount} 的口径与回落规则。 */
  pendingCount: number;
  /**
   * 上面那个数是不是 roster 给的。
   *
   * <p>用来分开两种「roster 没给」：**从来没给过**（老后端／首轮还在飞）要回落到审批队列，
   * 而**给过之后某一轮挂了**不能回落 —— 那会把一个 5 打成 2（审批队列是更窄的那个集合），
   * 抖动一下又回来。本 hook 对 tasks / unreadChats 的处理是同一个立场：一次失败沿用上一次的
   * 答案，而不是换一个口径的答案。
   */
  pendingFromRoster: boolean;
}

const EMPTY: Snapshot = {
  tenantId: null,
  tasks: [],
  unreadChats: { byAgent: {}, byConversation: {}, total: 0 },
  targetByAgent: {},
  approvalsAttention: false,
  pendingCount: 0,
  pendingFromRoster: false,
};

/**
 * Which sidebar row an agent_type belongs to, for TASKS.
 *
 * <p>The nav has one row per agent; the data has more agent types than that. Image Studio
 * (`image`) is a second window onto Maya's own instance, and Anna is two agents that share a name —
 * `concierge` answers the shopper on the storefront, `customer` answers the merchant in this
 * dashboard. An unlisted type maps to itself, so a new agent's tasks are never silently dropped:
 * a task dot can always be cleared by expanding its row in the Task Center, whatever produced it.
 */
const ROW_BY_AGENT_TYPE: Record<string, string> = {
  image: 'marketing',
  customer: 'concierge',
};

const rowKey = (agentType: string): string => ROW_BY_AGENT_TYPE[agentType] ?? agentType;

/**
 * Which sidebar row an agent_type belongs to, for CHATS — an ALLOWLIST, not a fallback.
 *
 * <p>A chat dot is cleared by opening that chat, so a conversation this app cannot open is a dot
 * with no way out. `chatbot-api`'s unread summary is not filtered by agent type, and
 * `digital_staff_conversations` holds more than the dashboard can show: `concierge` is the
 * STOREFRONT agent answering the merchant's shoppers — one shopper reply would light Anna's row
 * permanently, since nothing here ever opens that conversation to stamp `last_read_at`. `image`
 * and the Message Hub types (`seo`, `profit`) have no reporter either.
 *
 * <p>So only the three types a chat view actually mounts a reporter for count — the three
 * `useWsChat` is called with. Anything else contributes no chat dot at all, which is the safe
 * direction: a missing dot is recoverable, a permanent one is not.
 */
const CHAT_ROW_BY_AGENT_TYPE: Record<string, string> = {
  marketing: 'marketing',
  operation: 'operation',
  // Anna's merchant-facing console. Her row is keyed `concierge` in the nav (YEP-790).
  customer: 'concierge',
};

/**
 * The sidebar's red dots (PRD #4 / YEP-778 / YEP-803 / YEP-805).
 *
 * <p><b>A red dot means「有你没看过的东西」, not「你该动手了」.</b> The PRD is explicit that the dot
 * does not chase the merchant — that job belongs to the status labels and the filter counts — and it
 * is equally explicit that 状态点 ≠ 侧边栏红点, 两者不同步. So this no longer reads the agent status
 * roster to decide whether to light: the roster only knows each agent's CURRENT state, which meant a
 * task that simply finished never dotted at all, a second unread task under the same agent was
 * invisible, and any unread history was lost the moment the agent's next run changed its state.
 *
 * <p><b>Agent-row dots track CHATS only</b> (updated 2026-09-09); unread TASKS feed the Task Center aggregate, not the agent rows:
 *  - unread TASKS come from the task list itself, judged by `unreadStore` (new, or status changed
 *    since the merchant expanded the row);
 *  - unread CHATS come from the server (YEP-805), the same query the chat lists render, so a row and
 *    this aggregate cannot disagree.
 *
 * <p><b>The roster is still read</b>, for `targetByAgent` only: where a click should land is a
 * question about the agent's current state, not about what has been seen.
 *
 * <p>Polled on a 30s cadence; on failure the last known value is kept so a hiccup never flickers
 * every dot off.
 */
export function useAgentAttention(): AgentAttention {
  const tenantId = useAuthStore(s => s.user?.id);
  const [snapshot, setSnapshot] = useState(EMPTY);
  const taskReadLocally = useUnreadStore(s => s.taskReadLocally);
  const markTaskReadLocally = useUnreadStore(s => s.markTaskReadLocally);
  const onScreenChat = useUnreadStore(s => s.onScreenChat);
  const chatReadLocally = useUnreadStore(s => s.chatReadLocally);
  const clearLocalChatRead = useUnreadStore(s => s.clearLocalChatRead);

  // Monotonic token: a chat-read event fires a pull while the 30s poll's own is still in flight, and
  // the two race — four requests each, any of which can be the slow one. An older, pre-read answer
  // landing last would overwrite the post-read one and relight the row until the next tick.
  const pullSeq = useRef(0);
  // The latest task list, for converting acks between polls (the ack event carries only an id).
  const tasksRef = useRef<AgentTask[]>([]);
  tasksRef.current = snapshot.tasks;

  /**
   * Report `attentionAck`'s "the merchant glanced at this failure in its chat" to the server.
   *
   * <p>One-shot: the ack is consumed once reported, so a retry of the same row that fails again
   * lights up rather than being swallowed by an id that was acked weeks ago.
   */
  const convertAcks = useCallback(() => {
    const acked = ackedTaskIds();
    if (acked.size === 0 || !tenantId) return;
    for (const task of tasksRef.current) {
      if (!acked.has(task.id)) continue;
      // Consumed as soon as the task is SEEN, not only when it is reported. Skipping the consume
      // for a task that is no longer FAILED left the ack alive to be applied to that row's NEXT
      // failure — every merchant's pre-deploy ack list would drain onto failures nobody has seen.
      consumeAck(task.id);
      // Only FAILED is reported, matching what a glance is allowed to clear: a pending approval is
      // an outstanding action, and only expanding it in the Task Center reads it.
      if (task.status !== 'FAILED') continue;
      markTaskReadLocally(task.id, taskUnreadKey(task));
      void markAgentTaskRead(tenantId, task.id).catch(() => {
        /* a lost report costs a dot that clears the next time they reach it */
      });
    }
  }, [tenantId, markTaskReadLocally]);

  useEffect(() => {
    const onAck = () => convertAcks();
    window.addEventListener(ATTENTION_ACKED_EVENT, onAck);
    return () => window.removeEventListener(ATTENTION_ACKED_EVENT, onAck);
  }, [convertAcks]);

  useEffect(() => {
    if (!tenantId) {
      setSnapshot(EMPTY);
      return;
    }
    // Another merchant, in the same tab: drop the previous answer NOW rather than when the four
    // requests below land. Their chat and approval dots — and `targetByAgent`, which is clickable —
    // would otherwise stay on screen for the length of the fetch, or forever if one hangs.
    setSnapshot(prev => (prev.tenantId === tenantId ? prev : EMPTY));
    let cancelled = false;

    const pull = async (): Promise<ChatUnreadSummary | null> => {
      const seq = ++pullSeq.current;
      const [roster, approvals, tasks, unreadChats] = await Promise.all([
        getAgentStatusRoster(tenantId).catch(() => null),
        listApprovals(tenantId).catch(() => []),
        listAgentTasks(tenantId).catch(() => null),
        getChatUnreadSummary(tenantId).catch(() => null),
      ]);
      if (cancelled || seq !== pullSeq.current) return null;
      const targetByAgent: Record<string, AgentAttentionTarget> = {};
      for (const agent of roster?.agents ?? []) {
        if (agent.state === 'FAILED' || agent.state === 'AWAITING_APPROVAL') {
          targetByAgent[rowKey(agent.agentType)] = {
            taskId: agent.taskId,
            conversationId: agent.conversationId,
            failed: agent.state === 'FAILED',
          };
        }
      }
      const pendingApprovals = (approvals ?? []).filter(a => a.status === 'pending');
      // [Infra 1.5 #19] 待处理数 = roster 上每个 agent 的 pendingCount 之和。
      //
      // 只在**没有任何一个 agent 带这个字段**时才回落到审批队列 —— 不是 `?? 0`。这个分支是
      // 促发布窗口：本仓的 dev 会先于 chatbot-api 促到 test / main，那段时间后端的响应上
      // 还没有这个字段，而那时角标今天显示的是一个真数（审批条数）。`?? 0` 会把线上的角标
      // 从 N 打成 0，是一次实打实的回退。首轮 roster 就失败（catch → null）同样走这条回落 ——
      // 但**给过之后**的一次失败不走，见 `pendingFromRoster`。
      //
      // ⚠️ 批次子项不会被数两遍,这条已经核过,别再按「后端是整表计数」重提:喂出 `pendingCount`
      // 的那条聚合(`AgentTaskReadRepository.countByAgentAndStatus`)自己就带 `and t.parentRef is null`
      // ——「数交付物,不数子项」,与任务中心「待处理」页签排除 `parentRef` 行是同一条规则。
      //
      // 而**有**字段时就整个换掉，不是加在审批条数旁边：pendingCount 已经包含那批草稿
      // （审批队列就是 `content_drafts` 的投影），相加会重复计数。#19 之后 pendingCount 还多数了
      // Oscar 的预警与 Anna 的待接管，而回落分支只数审批条数，所以它**少数**这两类：对老后端无妨
      // （那时还没有这两类行），但新后端上「首轮 roster 就失败」也走这条回落 —— 那一刻角标会暂时
      // 只剩草稿数，直到下一次 roster 成功。
      const rosterPending = (roster?.agents ?? []).reduce<number | null>(
        (sum, agent) =>
          typeof agent.pendingCount === 'number' ? (sum ?? 0) + agent.pendingCount : sum,
        null
      );
      setSnapshot(prev => {
        // A failed fetch keeps the previous answer rather than blanking every dot — but only if it
        // is THIS merchant's. Carrying it across a tenant change would show one account another
        // account's dots for as long as the new one's request keeps failing.
        const same = prev.tenantId === tenantId;
        return {
          tenantId,
          tasks: tasks ?? (same ? prev.tasks : []),
          unreadChats: unreadChats ?? (same ? prev.unreadChats : EMPTY.unreadChats),
          targetByAgent,
          approvalsAttention: pendingApprovals.length > 0,
          // roster 给过之后的一次失败沿用上一次的数，不掉回审批队列那个更窄的口径。
          pendingCount:
            rosterPending ?? (same && prev.pendingFromRoster ? prev.pendingCount : pendingApprovals.length),
          pendingFromRoster: rosterPending !== null || (same && prev.pendingFromRoster),
        };
      });
      if (unreadChats) {
        // Memory hygiene: drop a chat note once the summary agrees it is read, or once it has aged
        // out. The render-time discount already ignores both, so this only bounds the map — read
        // fresh via getState() so the poll's own closure staleness can't leak in.
        const nowMs = Date.now();
        const chatNotes = useUnreadStore.getState().chatReadLocally;
        for (const [conversationId, note] of Object.entries(chatNotes)) {
          if (!unreadChats.byConversation[conversationId] || nowMs - note.at > CHAT_READ_TTL_MS) {
            clearLocalChatRead(conversationId);
          }
        }
      }
      if (tasks) {
        // Note: local read-markers are intentionally NOT cleared here. isTaskUnread() short-circuits
        // on task.unread !== true, and taskUnreadKey re-lights a task whose state genuinely changed,
        // so a marker is self-expiring. Clearing it here reopened the mirror direction where an
        // in-flight poll would relight a row the user had just read.
        tasksRef.current = tasks;
        convertAcks();
      }
      return unreadChats;
    };

    void pull();
    const timer = window.setInterval(() => void pull(), 30_000);
    // A read was just stamped: re-pull rather than carry a 30s-stale count. `onScreenChat` covers
    // the chat still on screen; this covers the one the merchant has just left.
    // Asked twice at most, for the same reason the chat list is (see `useWsChat`): a summary GET
    // already in flight when the read landed comes back from `chatbotGet`'s de-duplication cache
    // pre-stamp, and this aggregate would keep the row lit until the next 30s tick.
    const onChatRead = (event: Event) => {
      const readId = (event as CustomEvent<ChatReadEventDetail>).detail?.conversationId;
      void pull().then(summary => {
        if (readId && summary?.byConversation[readId]) void pull();
      });
    };
    window.addEventListener(CHAT_READ_EVENT, onChatRead);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener(CHAT_READ_EVENT, onChatRead);
    };
  }, [tenantId, clearLocalChatRead, convertAcks]);

  // Derived on READ rather than inside the poll, so expanding a row drops its dot immediately
  // instead of at the next fetch — and so the next fetch cannot resurrect it.
  const attentionByAgent: Record<string, boolean> = {};
  // [infra1.5 验收 #5③ · 2026-09-30] agent 名下有**没看过的对话或任务**就亮红点，全部看过才消。
  // 这推翻了 2026-09-09 那条「agent 行只看聊天、任务归 Task Center」的口径 —— 验收实测
  // Marketing / Operation 名下一堆未读任务，行上却一个点都没有。任务仍同时点亮 Task Center。
  let taskCenterAttention = false;
  for (const task of snapshot.tasks) {
    if (!isTaskUnread(taskReadLocally, task)) continue;
    taskCenterAttention = true;
    if (task.agentType) attentionByAgent[rowKey(task.agentType)] = true;
  }
  // Every conversation this tab currently treats as read though the server summary may still count
  // it: the one on screen, plus any read within the last CHAT_READ_TTL_MS. Deduped by id (a Map),
  // so a conversation that is BOTH on screen and locally-noted is subtracted exactly once —
  // over-subtracting would zero a row that still holds a DIFFERENT, genuinely-unread chat.
  const nowMs = Date.now();
  const discountRowById = new Map<string, string>();
  if (onScreenChat?.conversationId) {
    const row = CHAT_ROW_BY_AGENT_TYPE[onScreenChat.agentType];
    if (row) discountRowById.set(onScreenChat.conversationId, row);
  }
  for (const [conversationId, note] of Object.entries(chatReadLocally)) {
    if (nowMs - note.at > CHAT_READ_TTL_MS) continue;
    const row = CHAT_ROW_BY_AGENT_TYPE[note.agentType];
    if (row) discountRowById.set(conversationId, row);
  }
  const discountByRow: Record<string, number> = {};
  for (const [conversationId, row] of discountRowById) {
    // Only discount while the server STILL reports it unread; once the summary agrees, the raw count
    // has already dropped and a second subtraction would hide a sibling chat under the same row.
    if (snapshot.unreadChats.byConversation[conversationId]) {
      discountByRow[row] = (discountByRow[row] ?? 0) + 1;
    }
  }
  const unreadChatsByRow: Record<string, number> = {};
  for (const [agentType, count] of Object.entries(snapshot.unreadChats.byAgent)) {
    const row = CHAT_ROW_BY_AGENT_TYPE[agentType];
    // Not a conversation this app can open — see CHAT_ROW_BY_AGENT_TYPE.
    if (!row) continue;
    unreadChatsByRow[row] = (unreadChatsByRow[row] ?? 0) + count;
  }
  for (const [row, discount] of Object.entries(discountByRow)) {
    unreadChatsByRow[row] = Math.max(0, (unreadChatsByRow[row] ?? 0) - discount);
  }
  for (const [row, count] of Object.entries(unreadChatsByRow)) {
    if (count > 0) attentionByAgent[row] = true;
  }

  return {
    attentionByAgent,
    targetByAgent: snapshot.targetByAgent,
    // PRD #4: the Task Center / Platform aggregate is「有未读任务」— any unread task, not just a
    // failed one. It dims with the per-agent dots, so the two can never disagree about "seen".
    taskCenterAttention,
    approvalsAttention: snapshot.approvalsAttention,
    // 不因「读过」而消失：待处理是一件没做完的事，不是一条信息（旧的 approvals 点也从不 ack）。
    pendingCount: snapshot.pendingCount,
  };
}
